import {
  Box,
  Paper,
  Stack,
  Typography,
  useTheme,
  Collapse,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CopyableTypography from "components/Typography/CopyableTypography";
import { ActivityType } from "generated-graphql/graphql";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import {
  getRelevantPortalCredential,
  isLepcPortalActivity,
} from "../useRelevantPortalCredential";
import { useFulfillmentDetails } from "../useFulfillmentDetails";

/**
 * Infers the geppetto CLI action from the activity description when no job
 * exists yet. Mirrors the description conventions used in workflow-manager
 * state definitions.
 */
const inferActionFromDescription = (description?: string | null): string => {
  if (isLepcPortalActivity(description)) {
    return "inputDataLepc";
  }
  const desc = description?.toLowerCase() ?? "";
  if (desc.includes("download receipt")) {
    return "finalizeSubmission";
  }
  if (desc.includes("downloadreport")) {
    return "downloadReport";
  }
  return "inputData";
};

const getGeppettoAction = (
  activityType?: ActivityType,
  jobAction?: string | null,
  activityDescription?: string | null
) => {
  if (jobAction) {
    return jobAction;
  }
  if (activityType === ActivityType.Geppetto) {
    return inferActionFromDescription(activityDescription);
  }
  if (activityType === ActivityType.CollectEvidence) {
    return "collectEvidenceOfCompletion";
  }

  return "";
};

const GeppettoCommand = () => {
  const theme = useTheme();
  const { data } = useFulfillmentDetails();
  const { activityId } = useParams();
  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "fulfillment-geppetto-command-expanded",
    { defaultValue: true }
  );

  const viewingActivity = data?.tierIIReport.currentWorkflow?.activities.find(
    (a) => a.id === activityId
  );

  const isViewingGeppettoActivity =
    viewingActivity?.type === ActivityType.Geppetto ||
    viewingActivity?.type === ActivityType.CollectEvidence;

  const action = getGeppettoAction(
    viewingActivity?.type,
    viewingActivity?.currentJob?.action,
    viewingActivity?.description
  );

  const facilityId = data?.tierIIReport.facility.id;

  const reportKind = data?.tierIIReport.reportKind;

  const jobId = viewingActivity?.currentJob?.id;

  const relevantCredential = getRelevantPortalCredential(
    data?.tierIIReport.facility?.portalCredentials,
    viewingActivity
  );
  const credentialId = relevantCredential?.credential?.id;

  const geppettoCommand = useMemo(() => {
    const baseCmd = `yarn encamp geppetto --action ${action} --reporting-year ${data?.tierIIReport.reportingYear} --report-kind ${reportKind} --facility-id ${facilityId}`;

    const jobFlag = jobId ? `--job-id ${jobId}` : "--create-job";

    const credentialFlag = credentialId
      ? `--credential-id ${credentialId}`
      : "";

    return `${baseCmd} ${jobFlag} ${credentialFlag}`.trim();
  }, [
    action,
    facilityId,
    reportKind,
    jobId,
    credentialId,
    data?.tierIIReport.reportingYear,
  ]);

  if (!isViewingGeppettoActivity) return <></>;

  return (
    <Stack
      direction={"column"}
      component={Paper}
      elevation={0}
      sx={{ padding: theme.spacing(3) }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography variant="h6">Geppetto Command</Typography>
        <IconButton size="small" sx={{ ml: 1 }}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ mt: 3 }}>
          <CopyableTypography text={geppettoCommand} />
        </Box>
      </Collapse>
    </Stack>
  );
};

export { GeppettoCommand };
